import { Badge, Box, Button, Flex, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useSearchEquipmentOnline } from '../../hooks/data/useEquipment';
import { useAppToast } from '../../hooks/ui/useAppToast';
import { safeAnalytics } from '../../utils/analytics';
import { BowerSpinner } from '../ui/BowerSpinner';

export interface EquipmentSearchResult {
  readonly make: string;
  readonly model: string;
  readonly title: string;
  readonly description: string;
}

interface AddEquipmentSearchStepProps {
  /** Query and results are owned by the panel so they survive Back. */
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly results: ReadonlyArray<EquipmentSearchResult>;
  readonly onResultsChange: (results: ReadonlyArray<EquipmentSearchResult>) => void;
  readonly onSelect: (result: EquipmentSearchResult) => void;
  readonly onBack: () => void;
}

/**
 * Make / model on a search result. `make` and `model` are unbounded strings
 * off the model's JSON, and Badge is `nowrap` + `flexShrink: 0` with no
 * `maxW`, so a long value would draw outside the card: the cap is required.
 * `isTruncated` cannot work on the Badge itself (inline-flex makes the label
 * an anonymous flex item), hence the inner span; `title` keeps the full value
 * reachable once it clips.
 */
function SearchMetaBadge({ value }: { value: string }) {
  return (
    <Badge maxW="100%" minW={0} flexShrink={1} title={value}>
      <Box as="span" minW={0} isTruncated>
        {value}
      </Box>
    </Badge>
  );
}

/** "Search online": look the instrument up and fill the form from a result. */
export function AddEquipmentSearchStep({
  query,
  onQueryChange,
  results,
  onResultsChange,
  onSelect,
  onBack,
}: AddEquipmentSearchStepProps) {
  const toast = useAppToast();
  const searchOnline = useSearchEquipmentOnline();

  const search = async () => {
    if (!query.trim()) return;
    const result = await searchOnline.mutateAsync(query.trim());
    if (!result.success || !result.data) {
      toast({ title: 'Search failed', status: 'error' });
      return;
    }
    safeAnalytics.track(ANALYTICS_EVENTS.EQUIPMENT_SEARCH_ONLINE_USED, {
      has_results: result.data.length > 0,
    });
    onResultsChange(result.data);
    if (result.data.length === 0) {
      toast({
        title: 'No results found',
        description: 'Try a different search term',
        status: 'info',
      });
    }
  };

  return (
    <Flex direction="column" flex={1} minH={0}>
      {/* The content scrolls; the action row stays pinned below it. `p`/`m`
          keep focus rings inside the scroller's clip. */}
      <VStack spacing={4} align="stretch" flex={1} minH={0} overflowY="auto" p={1} m={-1}>
        <Text fontSize="sm" color="text.secondary">
          Search for your instrument to auto-fill its details.
        </Text>
        <Input
          size="sm"
          placeholder="e.g. Eppendorf 5810 R centrifuge"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search();
          }}
          autoFocus
        />
        {searchOnline.isPending && (
          <HStack justify="center" py={4}>
            <BowerSpinner size="sm" />
            <Text fontSize="sm" color="text.secondary">
              Searching…
            </Text>
          </HStack>
        )}
        {!searchOnline.isPending && results.length > 0 && (
          <VStack spacing={2} align="stretch">
            {results.map((result) => (
              <Box
                key={`${result.make}-${result.model}`}
                p={3}
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.default"
                cursor="pointer"
                _hover={{ borderColor: 'brand.primary', bg: 'brand.surface' }}
                onClick={() => onSelect(result)}
              >
                <Text fontSize="sm" fontWeight="medium" color="text.primary" noOfLines={1}>
                  {result.title}
                </Text>
                {(result.make || result.model) && (
                  <HStack spacing={1} mt={1} flexWrap="wrap" minW={0}>
                    {result.make && <SearchMetaBadge value={result.make} />}
                    {result.model && <SearchMetaBadge value={result.model} />}
                  </HStack>
                )}
                <Text fontSize="xs" color="text.muted" mt={1} noOfLines={2}>
                  {result.description}
                </Text>
              </Box>
            ))}
          </VStack>
        )}
      </VStack>
      <HStack justify="flex-end" spacing={2} pt={4} flexShrink={0}>
        <Button variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void search()}
          isLoading={searchOnline.isPending}
          isDisabled={!query.trim()}
        >
          Search
        </Button>
      </HStack>
    </Flex>
  );
}
